import React from "react";
import { __ } from "services/Translator";
import {
  TeaserHero,
  TeaserSection,
  TeaserGrid,
  FeatureCard,
  SampleCard,
} from "components/auth/teaser";
import { PlayIcon, TargetIcon, GiftIcon } from "./missionIcons";

/**
 * Logged-out Mission Hub — a marketing teaser, never a personal dashboard.
 * NON-personal by construction: generic example missions, no progress bars,
 * no metrics, no Claim/opt-in controls. The only actions are the real Sign-Up /
 * Log-in modals (wired via the FullLayout `setModal*PropsData` props).
 */

// Built per render, not at module scope: __() must run after the /translations
// payload is in localStorage or every label freezes to English for the session.
const getHowItWorks = () => [
  {
    icon: <PlayIcon size={22} />,
    title: __("Play the games you love"),
    description: __(
      "Every qualifying spin on your favourite slots and live tables moves a mission forward — automatically."
    ),
  },
  {
    icon: <TargetIcon size={22} />,
    title: __("Missions track themselves"),
    description: __(
      "Daily, weekly and communal challenges update in real time. No codes, no opt-in hoops."
    ),
  },
  {
    icon: <GiftIcon size={22} />,
    title: __("Claim real rewards"),
    description: __(
      "Bank free spins, Clover Coins and VIP points, then climb the Golden Clover tiers."
    ),
  },
];

// Illustrative examples only — no personal progress, no live values.
const getSampleMissions = () => [
  {
    badge: __("Daily"),
    title: __("Daily Spinner"),
    subtitle: __("Spin 50 rounds on any slot today."),
    footer: __("Reward: 20 Free Spins"),
  },
  {
    badge: __("Weekly"),
    title: __("Wager Warrior"),
    subtitle: __("Wager $500 across any qualifying game this week."),
    footer: __("Reward: 300 FS + 500 Clover Coins"),
  },
  {
    badge: __("Communal"),
    title: __("1,000× Hunter"),
    subtitle: __("Be the first to land a 1,000× single-spin multiplier."),
    footer: __("Reward: 500 FS + 1,000 CC"),
  },
  {
    badge: __("Live"),
    title: __("Red Hot Streak"),
    subtitle: __("Win 5 red bets in a row on any Live Roulette table."),
    footer: __("Reward: 75 Free Spins"),
  },
  {
    badge: __("New"),
    title: __("Reel In 750×"),
    subtitle: __("Hit a 750× multiplier on any Big Bass Bonanza title."),
    footer: __("Reward: 150 FS + 200 CC"),
  },
  {
    badge: __("VIP"),
    title: __("$5,000 Single-Spin"),
    subtitle: __("Place a single $5,000 spin — unlocked at Gold tier."),
    footer: __("Reward: 1,000 FS + 5,000 CC"),
    locked: true,
  },
];

export default function MissionsTeaser({
  setModalSignUpPropsData,
  setModalLoginPropsData,
}) {
  const HOW_IT_WORKS = getHowItWorks();
  const SAMPLE_MISSIONS = getSampleMissions();
  return (
    <div className="wheel_container mission-hub mission-hub--teaser container-xxl">
      <div className="page mission-hub__inner">
        <TeaserHero
          align="center"
          eyebrow={__("Golden Clover Missions")}
          title={__("Play the games you love. Earn as you go.")}
          subtitle={__(
            "Complete in-game missions to earn free spins, Clover Coins and VIP points — daily, weekly and communal challenges across every slot and live table."
          )}
          signUpLabel={__("Sign Up to start earning")}
          loginLabel={__("Log In")}
          setModalSignUpPropsData={setModalSignUpPropsData}
          setModalLoginPropsData={setModalLoginPropsData}
        >
          <p className="mission-teaser__legal">
            {__("18+ · Real-money play · Play responsibly · T&Cs apply")}
          </p>
        </TeaserHero>

        <TeaserSection
          align="center"
          eyebrow={__("How it works")}
          title={__("Three steps to your first reward")}
          subtitle={__(
            "No grinding a separate app — you earn while you play the casino you already enjoy."
          )}
        >
          <TeaserGrid minColWidth={240} gap={20}>
            {HOW_IT_WORKS.map((f) => (
              <FeatureCard
                key={f.title}
                icon={f.icon}
                title={f.title}
                description={f.description}
              />
            ))}
          </TeaserGrid>
        </TeaserSection>

        <TeaserSection
          align="center"
          eyebrow={__("A taste of what's live")}
          title={__("Example missions")}
          subtitle={__(
            "Just a preview — your personal missions appear the moment you sign in."
          )}
        >
          <TeaserGrid minColWidth={260} gap={20}>
            {SAMPLE_MISSIONS.map((m) => (
              <SampleCard
                key={m.title}
                badge={m.badge}
                title={m.title}
                subtitle={m.subtitle}
                footer={m.footer}
                locked={m.locked}
              />
            ))}
          </TeaserGrid>
        </TeaserSection>
      </div>
    </div>
  );
}
